import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Customer, Address } from '../types';
import { customerApi, addressApi } from '../api/machines';
import { useAuth } from '../context/AuthContext';
import { Toast, useToast } from '../components/Toast';
import { isValidNip, isValidPostCode, nipDigits } from '../utils/validation';

const MAX_LOGO_BYTES = 5 * 1024 * 1024;
const PAGE_SIZE = 12;

const Customers: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = !!user?.roles?.includes('ADMIN');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editCustomer, setEditCustomer] = useState<Customer | null>(null);
  const [previewCustomer, setPreviewCustomer] = useState<Customer | null>(null);
  const [page, setPage] = useState(1);
  const { toast, showToast, dismissToast } = useToast();
  const [confirmUnlink, setConfirmUnlink] = useState<Customer | null>(null);
  const [unlinking, setUnlinking] = useState(false);

  const load = useCallback(async () => {
    try {
      // Integrator admin sees only customers they service (have machines for);
      // super-admin sees the full directory.
      const { data } = await customerApi.getAll(!isAdmin);
      setCustomers(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  const doUnlink = async (c: Customer) => {
    setUnlinking(true);
    try {
      await customerApi.unlink(c.id);
      showToast('success', `Odpięto klienta „${c.companyName || c.nip}".`);
      setConfirmUnlink(null);
      load();
    } catch (e: any) {
      const machinesMsg = 'Nie można odpiąć tego klienta — Twój integrator ma przypisane jego maszyny. '
        + 'Najpierw usuń lub przenieś te maszyny, a potem odepnij klienta.';
      const msg = e?.response?.status === 409
        ? (e?.response?.data?.detail || e?.response?.data?.message || machinesMsg)
        : (e?.response?.data?.detail || e?.response?.data?.message || 'Nie udało się odpiąć klienta.');
      showToast('error', msg);
      setConfirmUnlink(null);
    } finally {
      setUnlinking(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(customers.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = customers.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
        <h1 className="page-title" style={{ margin: 0 }}>Klienci</h1>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          Dodaj klienta
        </button>
      </div>

      {loading ? (
        <div className="empty-state">Ładowanie...</div>
      ) : customers.length === 0 ? (
        <div className="empty-state">Brak klientów</div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
            {pageItems.map((c) => (
              <div
                key={c.id}
                className="card list-item-clickable"
                style={{ padding: '1.25rem', display: 'flex', gap: '1rem', alignItems: 'center' }}
                onClick={() => navigate(`/machines?nip=${encodeURIComponent(c.nip)}`)}
                title="Pokaż maszyny tego klienta"
              >
                <div style={{
                  width: 72, height: 72, flexShrink: 0,
                  borderRadius: 10, overflow: 'hidden',
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-surface-alt)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 6,
                }}>
                  {c.logoUrl ? (
                    <img src={c.logoUrl} alt={`Logo ${c.companyName || c.nip}`}
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  ) : (
                    <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>brak logo</span>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: '1.0625rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {c.companyName || 'Klient bez nazwy'}
                  </div>
                  <div className="mono" style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginTop: '0.15rem' }}>
                    NIP: {c.nip}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.6rem' }}>
                    <button
                      className="btn btn-primary"
                      onClick={(e) => { e.stopPropagation(); setPreviewCustomer(c); }}
                    >
                      Podgląd
                    </button>
                    {isAdmin && (
                      <button
                        className="btn btn-primary"
                        onClick={(e) => { e.stopPropagation(); setEditCustomer(c); }}
                      >
                        Edytuj
                      </button>
                    )}
                    {!isAdmin && (
                      <button
                        className="btn btn-primary"
                        onClick={(e) => { e.stopPropagation(); setConfirmUnlink(c); }}
                      >
                        Odepnij
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', marginTop: '1.5rem' }}>
              <button className="btn btn-secondary btn-sm" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)}>
                Poprzednia
              </button>
              <span className="item-meta pagination-info">Strona {safePage} z {totalPages}</span>
              <button className="btn btn-secondary btn-sm" disabled={safePage >= totalPages} onClick={() => setPage(safePage + 1)}>
                Następna
              </button>
            </div>
          )}
        </>
      )}

      {showModal && (
        <AddCustomerModal
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); setPage(1); load(); }}
        />
      )}

      {editCustomer && (
        <EditCustomerModal
          customer={editCustomer}
          onClose={() => setEditCustomer(null)}
          onSaved={() => { setEditCustomer(null); load(); }}
        />
      )}

      {previewCustomer && (
        <CustomerPreviewModal
          customer={previewCustomer}
          onClose={() => setPreviewCustomer(null)}
        />
      )}

      {confirmUnlink && (
        <div className="modal-overlay" onClick={() => { if (!unlinking) setConfirmUnlink(null); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <h2 className="modal-title">Odepnij klienta</h2>
            <p style={{ marginBottom: '0.75rem' }}>
              Czy odpiąć klienta <strong>{confirmUnlink.companyName || confirmUnlink.nip}</strong> od Twojego integratora?
            </p>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setConfirmUnlink(null)} disabled={unlinking}>
                Anuluj
              </button>
              <button type="button" className="btn btn-danger" onClick={() => doUnlink(confirmUnlink)} disabled={unlinking}>
                {unlinking ? 'Odpinam…' : 'Potwierdź'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} onDismiss={dismissToast} />
    </div>
  );
};

const Row: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', padding: '0.4rem 0', borderBottom: '1px solid var(--color-border)' }}>
    <span className="item-meta" style={{ flexShrink: 0 }}>{label}</span>
    <span style={{ textAlign: 'right', wordBreak: 'break-word' }}>{value || '—'}</span>
  </div>
);

const CustomerPreviewModal: React.FC<{ customer: Customer; onClose: () => void }> = ({ customer, onClose }) => {
  const [address, setAddress] = useState<Address | null>(null);
  const [loadingAddress, setLoadingAddress] = useState(!!customer.addressId);

  useEffect(() => {
    if (!customer.addressId) { setLoadingAddress(false); return; }
    let cancelled = false;
    addressApi.getById(customer.addressId as string)
      .then(({ data }) => { if (!cancelled) setAddress(data); })
      .catch(() => { /* ignore */ })
      .finally(() => { if (!cancelled) setLoadingAddress(false); });
    return () => { cancelled = true; };
  }, [customer.addressId]);

  const addressLine = address
    ? [
        [address.street, address.buildingNumber].filter(Boolean).join(' ')
          + (address.apartmentNumber ? `/${address.apartmentNumber}` : ''),
        [address.postalCode, address.city].filter(Boolean).join(' '),
        [address.region, address.country].filter(Boolean).join(', '),
      ].filter((s) => s && s.trim()).join(', ')
    : '';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="modal-title">Klient — podgląd</h2>

        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem' }}>
          <div style={{
            width: 72, height: 72, flexShrink: 0, borderRadius: 10, overflow: 'hidden', padding: 6,
            border: '1px solid var(--color-border)', background: 'var(--color-surface-alt)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {customer.logoUrl ? (
              <img src={customer.logoUrl} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            ) : (
              <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>brak logo</span>
            )}
          </div>
          <div style={{ fontWeight: 700, fontSize: '1.0625rem' }}>{customer.companyName || 'Klient bez nazwy'}</div>
        </div>

        <Row label="Nazwa firmy" value={customer.companyName} />
        <Row label="NIP" value={customer.nip} />
        {loadingAddress ? (
          <div className="item-meta" style={{ padding: '0.6rem 0' }}>Ładowanie adresu…</div>
        ) : customer.addressId ? (
          <>
            <Row label="Adres" value={addressLine} />
            <Row label="Ulica" value={address?.street} />
            <Row label="Nr budynku" value={address?.buildingNumber} />
            <Row label="Nr lokalu" value={address?.apartmentNumber} />
            <Row label="Kod pocztowy" value={address?.postalCode} />
            <Row label="Miasto" value={address?.city} />
            <Row label="Województwo" value={address?.region} />
            <Row label="Kraj" value={address?.country} />
          </>
        ) : (
          <div className="item-meta" style={{ padding: '0.6rem 0' }}>Brak adresu.</div>
        )}

        <div className="modal-actions">
          <button type="button" className="btn btn-primary" onClick={onClose}>Zamknij</button>
        </div>
      </div>
    </div>
  );
};

const emptyAddress = { street: '', buildingNumber: '', apartmentNumber: '', city: '', postalCode: '', country: 'Polska' };

const AddCustomerModal: React.FC<{ onClose: () => void; onSaved: () => void }> = ({ onClose, onSaved }) => {
  const [form, setForm] = useState({ nip: '', companyName: '', ...emptyAddress });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!isValidNip(form.nip)) {
      setError('Nieprawidłowy NIP — podaj 10 cyfr z poprawną sumą kontrolną.');
      return;
    }
    if (form.postalCode.trim() && !isValidPostCode(form.postalCode)) {
      setError('Nieprawidłowy kod pocztowy — wymagany format 00-000.');
      return;
    }

    setLoading(true);
    try {
      const { nip, companyName, ...addr } = form;
      // Address is optional — only create one if the user actually filled it in.
      const hasAddress = [addr.street, addr.buildingNumber, addr.city, addr.postalCode].some((v) => v.trim());
      let addressId: string | undefined;
      if (hasAddress) {
        const { data: address } = await addressApi.create(addr);
        addressId = address.id;
      }
      await customerApi.create({
        nip: nipDigits(nip),
        companyName: companyName.trim() || null,
        ...(addressId ? { addressId } : {}),
      });
      onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.response?.data?.message || 'Nie udało się dodać klienta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="modal-title">Nowy klient</h2>
        <form onSubmit={handleSubmit}>
          {error && <div className="auth-error">{error}</div>}

          <div className="form-group">
            <label className="form-label">Nazwa firmy</label>
            <input className="form-input" name="companyName" value={form.companyName} onChange={handleChange} />
          </div>

          <div className="form-group">
            <label className="form-label">NIP</label>
            <input className="form-input" name="nip" value={form.nip} onChange={handleChange} required inputMode="numeric" maxLength={13} placeholder="1234567890" />
          </div>

          <div className="item-meta" style={{ margin: '0.25rem 0 0.75rem' }}>
            Jeśli NIP już istnieje w systemie, dołączysz do wspólnego wpisu (nazwa i logo pozostaną). Adres — opcjonalnie.
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            <div className="form-group">
              <label className="form-label">Ulica</label>
              <input className="form-input" name="street" value={form.street} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label className="form-label">Nr budynku</label>
              <input className="form-input" name="buildingNumber" value={form.buildingNumber} onChange={handleChange} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            <div className="form-group">
              <label className="form-label">Nr lokalu</label>
              <input className="form-input" name="apartmentNumber" value={form.apartmentNumber} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label className="form-label">Kod pocztowy</label>
              <input className="form-input" name="postalCode" value={form.postalCode} onChange={handleChange} inputMode="numeric" maxLength={6} placeholder="00-000" />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            <div className="form-group">
              <label className="form-label">Miasto</label>
              <input className="form-input" name="city" value={form.city} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label className="form-label">Kraj</label>
              <input className="form-input" name="country" value={form.country} onChange={handleChange} />
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Anuluj</button>
            <button type="submit" className="btn btn-primary" disabled={loading || !form.nip.trim()}>
              {loading ? 'Zapisuję...' : 'Dodaj klienta'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditCustomerModal: React.FC<{ customer: Customer; onClose: () => void; onSaved: () => void }> = ({ customer, onClose, onSaved }) => {
  const [form, setForm] = useState({
    nip: customer.nip,
    companyName: customer.companyName || '',
    street: '', buildingNumber: '', apartmentNumber: '',
    city: '', postalCode: '', region: '', country: '',
  });
  const [address, setAddress] = useState<Address | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingAddress, setLoadingAddress] = useState(!!customer.addressId);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(customer.logoUrl);
  const [removeLogo, setRemoveLogo] = useState(false);

  const onLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setError('');
    if (!file) { setLogoFile(null); setLogoPreview(customer.logoUrl); return; }
    if (!file.type.startsWith('image/')) { setError('Logo musi być obrazem'); e.target.value = ''; return; }
    if (file.size > MAX_LOGO_BYTES) { setError('Logo przekracza 5 MB'); e.target.value = ''; return; }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    setRemoveLogo(false);
  };

  const onRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setRemoveLogo(true);
    const fileInput = document.getElementById('edit-customer-logo-input') as HTMLInputElement | null;
    if (fileInput) fileInput.value = '';
  };

  useEffect(() => {
    if (!customer.addressId) { setLoadingAddress(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await addressApi.getById(customer.addressId as string);
        if (cancelled) return;
        setAddress(data);
        setForm((f) => ({
          ...f,
          street: data.street || '',
          buildingNumber: data.buildingNumber || '',
          apartmentNumber: data.apartmentNumber || '',
          city: data.city || '',
          postalCode: data.postalCode || '',
          region: data.region || '',
          country: data.country || '',
        }));
      } catch {
        if (!cancelled) setError('Nie udało się załadować adresu');
      } finally {
        if (!cancelled) setLoadingAddress(false);
      }
    })();
    return () => { cancelled = true; };
  }, [customer.addressId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!isValidNip(form.nip)) {
      setError('Nieprawidłowy NIP — podaj 10 cyfr z poprawną sumą kontrolną.');
      return;
    }
    if (form.postalCode.trim() && !isValidPostCode(form.postalCode)) {
      setError('Nieprawidłowy kod pocztowy — wymagany format 00-000.');
      return;
    }

    setLoading(true);
    try {
      const addrPayload = {
        street: form.street, buildingNumber: form.buildingNumber, apartmentNumber: form.apartmentNumber,
        city: form.city, postalCode: form.postalCode, region: form.region, country: form.country,
      };
      const hasAddressInput = [form.street, form.buildingNumber, form.city, form.postalCode].some((v) => v.trim());
      let newAddressId: string | undefined;
      if (address) {
        await addressApi.update(address.id, addrPayload);
      } else if (hasAddressInput) {
        const { data } = await addressApi.create(addrPayload);
        newAddressId = data.id;
      }
      await customerApi.update(customer.id, {
        nip: nipDigits(form.nip),
        companyName: form.companyName.trim() || null,
        ...(newAddressId ? { addressId: newAddressId } : {}),
      });
      if (logoFile) {
        await customerApi.uploadLogo(customer.id, logoFile);
      } else if (removeLogo && customer.logoUrl) {
        await customerApi.deleteLogo(customer.id);
      }
      onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.response?.data?.message || 'Nie udało się zapisać zmian');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="modal-title">Edytuj klienta</h2>
        {loadingAddress ? (
          <div className="empty-state">Ładowanie danych...</div>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && <div className="auth-error">{error}</div>}

            <div className="form-group">
              <label className="form-label">Nazwa firmy</label>
              <input className="form-input" name="companyName" value={form.companyName} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label className="form-label">NIP</label>
              <input className="form-input" name="nip" value={form.nip} onChange={handleChange} required inputMode="numeric" maxLength={13} placeholder="1234567890" />
            </div>

            <div className="item-meta" style={{ margin: '0.25rem 0 0.75rem' }}>Adres</div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
              <div className="form-group">
                <label className="form-label">Ulica</label>
                <input className="form-input" name="street" value={form.street} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label className="form-label">Nr budynku</label>
                <input className="form-input" name="buildingNumber" value={form.buildingNumber} onChange={handleChange} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
              <div className="form-group">
                <label className="form-label">Nr lokalu</label>
                <input className="form-input" name="apartmentNumber" value={form.apartmentNumber} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label className="form-label">Kod pocztowy</label>
                <input className="form-input" name="postalCode" value={form.postalCode} onChange={handleChange} inputMode="numeric" maxLength={6} placeholder="00-000" />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
              <div className="form-group">
                <label className="form-label">Miasto</label>
                <input className="form-input" name="city" value={form.city} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label className="form-label">Województwo</label>
                <input className="form-input" name="region" value={form.region} onChange={handleChange} />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Kraj</label>
              <input className="form-input" name="country" value={form.country} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label className="form-label">Logo klienta (max 5 MB)</label>
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{
                  width: 72, height: 72, flexShrink: 0, borderRadius: 8, padding: 4,
                  border: '1px solid var(--color-border)', background: 'var(--color-surface-alt)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
                }}>
                  {logoPreview ? (
                    <img src={logoPreview} alt="Podgląd logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  ) : (
                    <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>brak</span>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', flex: 1, minWidth: 0 }}>
                  <input id="edit-customer-logo-input" type="file" accept="image/*" className="form-input" onChange={onLogoChange} />
                  {(logoPreview || customer.logoUrl) && (
                    <button type="button" className="btn btn-secondary btn-sm" style={{ alignSelf: 'flex-start' }} onClick={onRemoveLogo}>
                      Usuń logo
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>Anuluj</button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Zapisuję...' : 'Zapisz'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default Customers;
